import React, { useId } from 'react';
import { Box, useTheme } from '@mui/material';

import { reefColors } from '../../themes';

/**
 * Signature chrome for Reef Modernism — a woven lattice under a set of reef
 * contour sweeps, the way a bathymetric chart draws a shoal.
 *
 * Why these two motifs together: a wave alone reads as a loading shimmer in a
 * canvas app, and a lattice alone reads as a grid overlay. The straight warp
 * supplies the *industrial* half of "tropical-industrial" and the contours
 * supply the *tropical* half, which is literally the register the brand brief
 * asks for.
 *
 * Why SVG rather than canvas: `ConstellationBackground` earns its canvas — it
 * runs mouse attractors and a per-frame node simulation. This motif is static
 * geometry, so a canvas would cost a compositing layer and a resize observer
 * for nothing. The whole component is one Box, one SVG, one pattern and six
 * paths, it scales to any panel without a measurement pass, and its colors are
 * theme tokens rather than the hardcoded RGB triples a canvas would need.
 *
 * Why nothing animates: stillness is the correct default here, so
 * `prefers-reduced-motion` is satisfied by construction rather than by a media
 * query. A drifting shoal behind an image editor reads as a loading state.
 *
 * Terracotta appears on two of the six sweeps and nowhere else: the mockups
 * keep the accent for the action being invited, and the canvas region already
 * spends its terracotta on the operation bar, so the motif stays almost
 * entirely in the warm ink of the ground it sits on.
 *
 * Unlike the Constellations precedent this runs in *both* color modes — the
 * motif is drawn geometry, not a light effect, so it reads the same way on
 * stone as it does on ink.
 */
const ReefBackground: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Multiple mounts (studio + a future hub surface) would otherwise collide on
  // the `defs` ids and silently share one pattern.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');

  const weave = isDark ? reefColors.reefWeaveDark : reefColors.reefWeaveLight;
  const contour = isDark ? reefColors.reefContourDark : reefColors.reefContourLight;
  const contourAccent = isDark
    ? reefColors.reefContourAccentDark
    : reefColors.reefContourAccentLight;

  return (
    <Box
      aria-hidden
      sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1200 800"
        preserveAspectRatio="none"
        focusable="false"
        style={{ display: 'block' }}
      >
        <defs>
          <pattern
            id={`reef-weave-${uid}`}
            width="26"
            height="26"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <path
              d="M0 0 V26 M0 0 H26"
              stroke={weave}
              strokeWidth="1"
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
          </pattern>

          {/* Fade the lattice out toward the middle: the motif frames the work
              rather than sitting underneath it. */}
          <radialGradient id={`reef-vignette-${uid}`} cx="50%" cy="50%" r="72%">
            <stop offset="0%" stopColor="#000000" stopOpacity="0" />
            <stop offset="45%" stopColor="#000000" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="1" />
          </radialGradient>
          <mask id={`reef-mask-${uid}`}>
            <rect width="1200" height="800" fill={`url(#reef-vignette-${uid})`} />
          </mask>
        </defs>

        <rect
          width="1200"
          height="800"
          fill={`url(#reef-weave-${uid})`}
          mask={`url(#reef-mask-${uid})`}
        />

        {/* Reef contours — long bathymetric sweeps at the brief's 1–2pt weight.
            Every third line takes the accent so the field isn't monotone.

            `vector-effect` is NOT an inherited attribute, so it has to sit on
            each path rather than on the group: with preserveAspectRatio="none"
            the viewBox scales x and y independently, and an inherited-looking
            declaration on the <g> would silently do nothing — letting the
            mostly-horizontal sweeps thin to sub-pixel in a short, wide canvas
            and disappear. */}
        <g fill="none" strokeWidth="1.25" strokeLinecap="round" mask={`url(#reef-mask-${uid})`}>
          <path
            d="M-40 150 C 220 60, 430 250, 700 170 S 1120 90, 1240 200"
            stroke={contour}
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M-40 250 C 230 165, 450 350, 720 268 S 1130 190, 1240 296"
            stroke={contour}
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M-40 352 C 240 272, 470 452, 740 368 S 1140 292, 1240 394"
            stroke={contourAccent}
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M-40 460 C 250 384, 490 556, 760 472 S 1150 398, 1240 498"
            stroke={contour}
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M-40 572 C 260 500, 510 664, 780 580 S 1160 508, 1240 606"
            stroke={contour}
            vectorEffect="non-scaling-stroke"
          />
          <path
            d="M-40 690 C 270 620, 530 776, 800 692 S 1170 622, 1240 716"
            stroke={contourAccent}
            vectorEffect="non-scaling-stroke"
          />
        </g>
      </svg>
    </Box>
  );
};

export default ReefBackground;
